import { createApi } from "@reduxjs/toolkit/query/react";
import { FilterValue } from "interfaces/FilterTypes";
import { FeePlanList, FeePlansForm } from "interfaces/Revenue";
import { customBaseQuery, handleQueryLifecycle } from "store/utils";

type Id = string | number;
export interface SetupFeePlanFlow {
  id: string;
  clientName: string;
  clientCode: string;
  client: number;
  portfolio: number;
  portfolioName: string;
  portfolioId: string;
  feePlan: string;
  feePlanName: string;
  startingDate?: string;
  totalFeeCollected: number;
  totalFeeGenerated: number;
  partnerName: string;
}
interface SetupFeePlanFlowResponse {
  totalCount: number;
  data: SetupFeePlanFlow[];
}

export const feePlanApi = createApi({
  reducerPath: "feePlanApi",
  baseQuery: customBaseQuery,
  tagTypes: [
    "FeePlans",
    "FeePlan",
    "SetUpFeePlanFlow",
    "SetUpFeePlanFlows",
    "RevenueByPortfolio",
    "PortfolioHighWaterMark",
  ],
  endpoints: (builder) => ({
    getFeePlans: builder.query<any, any>({
      query: (params) => ({ url: "/api/revenue/fee-plan/", params }),
      // keepUnusedDataFor: 0,
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: FeePlanList) => ({
                type: "FeePlan" as const,
                id: item.id,
              })),
              { type: "FeePlans" as const, id: "LIST" },
            ]
          : [{ type: "FeePlans" as const, id: "LIST" }];
      },
    }),

    getFeePlanById: builder.query<any, string | number>({
      query: (id) => `/api/revenue/fee-plan/${id}/`,
      providesTags: (_res, _err, id) => [{ type: "FeePlan", id }],
    }),

    createFeePlan: builder.mutation<any, Partial<FeePlansForm>>({
      query: (body) => ({
        url: "/api/revenue/fee-plan/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeePlans", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    updateFeePlan: builder.mutation<
      any,
      { id: Id; body: Partial<FeePlansForm> }
    >({
      query: ({ id, body }) => ({
        url: `/api/revenue/fee-plan/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        result
          ? [
              { type: "FeePlan", id },
              { type: "FeePlans", id: "LIST" },
            ]
          : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    deleteFeePlan: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/api/revenue/fee-plan/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "FeePlans", id: "LIST" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    setUpFeePlanFlow: builder.query<SetupFeePlanFlowResponse, FilterValue>({
      query: (params) => ({ url: "/api/revenue/setup-fee-plan-flow/", params }),
      keepUnusedDataFor: 0,
      providesTags: (result) => {
        return result?.data
          ? [
              ...result.data?.map((item: SetupFeePlanFlow) => ({
                type: "SetUpFeePlanFlow" as const,
                id: item.id,
              })),
              { type: "SetUpFeePlanFlows" as const, id: "LIST" },
            ]
          : [{ type: "SetUpFeePlanFlows" as const, id: "LIST" }];
      },
    }),

    getRevenueByPortfolio: builder.query({
      keepUnusedDataFor: 0,
      query: (id) => `/api/revenue/portfolio-revenue/${id}/`,
      providesTags: (_res, _err, id) => [{ type: "RevenueByPortfolio", id }],
    }),

    changeBillingDate: builder.mutation({
      query: ({ portfolioId, startingDate }) => ({
        url: `/api/revenue/portfolio-revenue/${portfolioId}/update-starting-date/`,
        method: "PATCH",
        body: { startingDate },
      }),
      invalidatesTags: (result, error, { portfolioId }) => {
        console.log(result);
        return result ? [{ type: "RevenueByPortfolio", id: portfolioId }] : [];
      },
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    changeFeePlan: builder.mutation({
      query: (data) => ({
        url: "/api/revenue/setup-fee-plan-flow/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "RevenueByPortfolio" }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    updateRevenueSharing: builder.mutation({
      query: ({
        portfolioId,
        managementFeeSharingPercentage,
        performanceFeeSharingPercentage,
      }) => ({
        url: `/api/revenue/portfolio-revenue/${portfolioId}/update-sharing-percentage/`,
        method: "PATCH",
        body: {
          managementFeeSharingPercentage: managementFeeSharingPercentage,
          performanceFeeSharingPercentage: performanceFeeSharingPercentage,
        },
      }),
      invalidatesTags: (result, error, { portfolioId }) =>
        result ? [{ type: "RevenueByPortfolio", id: portfolioId }] : [],
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),

    getFeePlanHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (id) => `/api/revenue/portfolio-revenue/${id}/fee-plan-history/`,
    }),
  }),
});

export const {
  useGetFeePlansQuery,
  useCreateFeePlanMutation,
  useUpdateFeePlanMutation,
  useDeleteFeePlanMutation,
  useGetFeePlanByIdQuery,
  useSetUpFeePlanFlowQuery,
  useGetRevenueByPortfolioQuery,
  useChangeBillingDateMutation,
  useChangeFeePlanMutation,
  useUpdateRevenueSharingMutation,
  useGetFeePlanHistoryQuery,
} = feePlanApi;
